import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Plus, Send, Clock, History, Settings, 
  ArrowLeft, Edit, Trash2, Image as ImageIcon 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PushNotifications = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [autoTriggers, setAutoTriggers] = useState([]);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Template form state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    message: '',
    image_id: null,
    cta_text: '',
    cta_link: ''
  });
  
  // Send notification state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({
    template_id: 'custom',
    custom_title: '',
    custom_message: '',
    target_audience: 'all',
    schedule_at: null
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }

    setLoading(true);
    try {
      if (activeTab === 'templates') {
        const response = await fetch(`${API_URL}/api/push-notifications/templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setTemplates(data);
      } else if (activeTab === 'history') {
        const response = await fetch(`${API_URL}/api/push-notifications/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setNotificationHistory(data);
      } else if (activeTab === 'auto-triggers') {
        const response = await fetch(`${API_URL}/api/push-notifications/auto-triggers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setAutoTriggers(data);
      }
      
      // Always fetch subscription count
      const countResponse = await fetch(`${API_URL}/api/push-notifications/subscription-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const countData = await countResponse.json();
      setSubscriptionCount(countData.count);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setLoading(true);
    
    try {
      const url = editingTemplate 
        ? `${API_URL}/api/push-notifications/templates/${editingTemplate.id}`
        : `${API_URL}/api/push-notifications/templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated!' : 'Template created!');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setTemplateForm({
          name: '',
          title: '',
          message: '',
          image_id: null,
          cta_text: '',
          cta_link: ''
        });
        fetchData();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    const token = localStorage.getItem('customerAdminToken');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/push-notifications/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Template deleted!');
        fetchData();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setLoading(true);
    
    try {
      // Prepare the request body
      const requestBody = {
        ...sendForm,
        template_id: sendForm.template_id === 'custom' ? null : sendForm.template_id
      };
      
      const response = await fetch(`${API_URL}/api/push-notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        if (result.status === 'scheduled') {
          toast.success(`Notification scheduled for ${new Date(sendForm.schedule_at).toLocaleString()}`);
        } else if (result.status === 'sent') {
          toast.success(`Notification sent to ${result.total_sent} subscribers!`);
        } else if (result.status === 'no_subscribers') {
          toast.warning('No subscribers found for the selected audience');
        }
        setShowSendModal(false);
        setSendForm({
          template_id: 'custom',
          custom_title: '',
          custom_message: '',
          target_audience: 'all',
          schedule_at: null
        });
        fetchData();
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const token = localStorage.getItem('customerAdminToken');
    const formData = new FormData();
    formData.append('file', file);
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/push-notifications/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        setTemplateForm({ ...templateForm, image_id: data.image_id });
        toast.success('Image uploaded!');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      title: template.title,
      message: template.message,
      image_id: template.image_id,
      cta_text: template.cta_text || '',
      cta_link: template.cta_link || ''
    });
    setShowTemplateModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/customer-admin/dashboard')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Bell className="h-6 w-6" />
                  Push Notifications
                </h1>
                <p className="text-blue-100 text-sm mt-1">
                  {subscriptionCount} active subscribers
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowSendModal(true)}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Notification
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="bg-white rounded-lg shadow-sm p-1 flex space-x-1">
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'templates'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <History className="h-4 w-4 inline mr-2" />
            History
          </button>
          <button
            onClick={() => setActiveTab('auto-triggers')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'auto-triggers'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Clock className="h-4 w-4 inline mr-2" />
            Auto Triggers
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'templates' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Notification Templates</h2>
              <Button onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({
                  name: '',
                  title: '',
                  message: '',
                  image_id: null,
                  cta_text: '',
                  cta_link: ''
                });
                setShowTemplateModal(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>
            
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : templates.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No templates yet. Create your first template!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="line-clamp-1">{template.title}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.message}</p>
                      {template.image_id && (
                        <div className="mb-4">
                          <img 
                            src={`${API_URL}/api/push-notifications/image/${template.image_id}`}
                            alt="Template"
                            className="w-full h-32 object-cover rounded"
                          />
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editTemplate(template)}
                          className="flex-1"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="flex-1 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Notification History</h2>
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : notificationHistory.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No notifications sent yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notificationHistory.map((notification) => (
                  <Card key={notification.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold">{notification.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>Sent: {new Date(notification.sent_at).toLocaleString()}</span>
                            <span>Audience: {notification.target_audience}</span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-medium text-green-600">
                            {notification.total_sent} sent
                          </div>
                          {notification.total_failed > 0 && (
                            <div className="text-sm text-red-600">
                              {notification.total_failed} failed
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'auto-triggers' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Auto-Trigger Rules</h2>
            <Card>
              <CardContent className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Auto-trigger functionality coming soon!</p>
                <p className="text-sm text-gray-500 mt-2">
                  Set up rules to automatically send notifications based on user behavior
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create/Edit Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Create a reusable template for push notifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="name" className="text-sm">Template Name</Label>
              <Input
                id="name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Welcome Offer, Special Discount"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="title" className="text-sm">Notification Title</Label>
              <Input
                id="title"
                value={templateForm.title}
                onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                placeholder="e.g., Special Offer Just for You!"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="message" className="text-sm">Notification Message</Label>
              <Textarea
                id="message"
                value={templateForm.message}
                onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                placeholder="Enter your notification message..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="image" className="text-sm">Image (Optional)</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="flex-1"
                />
                {templateForm.image_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTemplateForm({ ...templateForm, image_id: null })}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {templateForm.image_id && (
                <img
                  src={`${API_URL}/api/push-notifications/image/${templateForm.image_id}`}
                  alt="Preview"
                  className="mt-2 w-full h-32 object-cover rounded"
                />
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cta_text" className="text-sm">CTA Text (Optional)</Label>
                <Input
                  id="cta_text"
                  value={templateForm.cta_text}
                  onChange={(e) => setTemplateForm({ ...templateForm, cta_text: e.target.value })}
                  placeholder="e.g., View Offer"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cta_link" className="text-sm">CTA Link (Optional)</Label>
                <Input
                  id="cta_link"
                  value={templateForm.cta_link}
                  onChange={(e) => setTemplateForm({ ...templateForm, cta_link: e.target.value })}
                  placeholder="e.g., /plans"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} disabled={loading}>
              {loading ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Notification Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Push Notification</DialogTitle>
            <DialogDescription>
              Send a notification to your subscribers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="template">Select Template (Optional)</Label>
              <Select
                value={sendForm.template_id}
                onValueChange={(value) => setSendForm({ ...sendForm, template_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template or create custom" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Message</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {sendForm.template_id === 'custom' && (
              <>
                <div>
                  <Label htmlFor="custom_title">Title</Label>
                  <Input
                    id="custom_title"
                    value={sendForm.custom_title}
                    onChange={(e) => setSendForm({ ...sendForm, custom_title: e.target.value })}
                    placeholder="Notification title"
                  />
                </div>
                
                <div>
                  <Label htmlFor="custom_message">Message</Label>
                  <Textarea
                    id="custom_message"
                    value={sendForm.custom_message}
                    onChange={(e) => setSendForm({ ...sendForm, custom_message: e.target.value })}
                    placeholder="Notification message"
                    rows={3}
                  />
                </div>
              </>
            )}
            
            <div>
              <Label htmlFor="audience">Target Audience</Label>
              <Select
                value={sendForm.target_audience}
                onValueChange={(value) => setSendForm({ ...sendForm, target_audience: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subscribers</SelectItem>
                  <SelectItem value="inactive_visitors">Inactive Visitors (No Action)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="schedule">Schedule (Optional)</Label>
              <Input
                id="schedule"
                type="datetime-local"
                value={sendForm.schedule_at || ''}
                onChange={(e) => setSendForm({ ...sendForm, schedule_at: e.target.value || null })}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to send immediately</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendNotification} disabled={loading}>
              <Send className="h-4 w-4 mr-2" />
              {sendForm.schedule_at ? 'Schedule' : 'Send Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PushNotifications;
